import {useEffect} from 'react';

import sdk from '@/sdk';
import * as store from '@/store';
import {ServerConfig} from '@/config/routers';

// 裁剪信息
interface Clip {
    x: number;
    y: number;
    width: number;
    height: number;
}

// 主题色
interface ThemeColor {
    control: string;
    bgColor: string;
}

// 封面相关信息
interface CoverOption {
    width: number;
    height: number;
    clip: Clip;
    themeColor: ThemeColor;
}

// 封面上传-编辑器返回参数
interface UploadOption {
    setProgress: (progress: number) => void;
}

// 历史记录-封面完整信息（给编辑器的数据格式）
interface Cover extends CoverOption {
    url: string;
    coverId: string;
}

// 历史记录-封面完整信息（服务端返回的数据格式）
interface ServerCover {
    url: string;
    coverId: string;
    coverOption: string; // 封面额外信息，由 OriginCoverOption 通过 JSON.stringify 转成的字符串
}

/**
 * 封面配置逻辑
 * @param docGuid 文档 ID
 * @returns
 */
export const useCoverConfig = (docGuid: string) => {
    useEffect(() => {
        return () => {
            store.setShowDocCover(false);
            store.setShowSlideCover(false);
        };
    }, [docGuid]);

    return {
        // 封面历史记录获取接口
        getUploadRecords: async (): Promise<Cover[]> => {
            try {
                const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
                if (!sdk.as.user?.userGuid || !rteToken) {
                    throw new Error('用户 id 或 rte token 为空');
                }

                const res = await sdk.as.request({
                    method: 'get',
                    url: `${ServerConfig.attach}/cover/list`,
                    params: {
                        userGuid: sdk.as.user.userGuid,
                    },
                    headers: {
                        Authorization: rteToken,
                    },
                });

                if (Array.isArray(res)) {
                    return res.map((item: ServerCover) => {
                        const {coverId, url, coverOption} = item;

                        const originCoverOption: CoverOption = JSON.parse(coverOption);
                        const {width, height, clip, themeColor} = originCoverOption;

                        return {
                            coverId,
                            url,
                            width,
                            height,
                            clip,
                            themeColor,
                        };
                    });
                } else {
                    throw new Error('封面历史记录数据异常');
                }
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        // 封面上传接口
        upload: async (file: File, option: UploadOption) => {
            try {
                const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
                if (!sdk.as.user?.userGuid || !rteToken) {
                    throw new Error('用户 id 或 rte token 为空');
                }

                const {setProgress} = option;

                const formData = new FormData();
                formData.append('file', file);
                formData.append('userGuid', sdk.as.user.userGuid);
                formData.append('docGuid', docGuid);

                const res = await sdk.as.request({
                    method: 'post',
                    url: `${ServerConfig.attach}/cover/upload`,
                    data: formData,
                    headers: {
                        Authorization: rteToken,
                        'content-type': 'multipart/form-data;charset=UTF-8',
                    },
                    timeout: 60 * 1000,
                    onUploadProgress: (progressEvent: any) => {
                        if (progressEvent.lengthComputable) {
                            const {loaded, total} = progressEvent;
                            const progress = (loaded / total) || 0;

                            setProgress(progress);
                        }
                    },
                });

                if (res?.coverId && res?.url) {
                    return {
                        coverId: res.coverId,
                        url: res.url,
                    };
                } else {
                    throw new Error('封面上传异常');
                }
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        // 封面应用回调
        save: async (coverId: string, option: CoverOption) => {
            try {
                const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
                if (!sdk.as.user?.userGuid || !rteToken) {
                    throw new Error('用户 id 或 rte token 为空');
                }

                const {width, height, clip, themeColor} = option;
                const originCoverOption: CoverOption = {
                    width,
                    height,
                    clip,
                    themeColor,
                };

                await sdk.as.request({
                    method: 'post',
                    url: `${ServerConfig.attach}/cover/publish`,
                    data: {
                        userGuid: sdk.as.user.userGuid,
                        coverId,
                        coverOption: JSON.stringify(originCoverOption),
                    },
                    headers: {
                        Authorization: rteToken,
                    },
                });
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        // 移除封面时回调
        onRemoveCover: (value: boolean) => {
            // 切换封面显示状态已从cover:change通知中处理，此处无需处理
            // store.setIsShowCover(false);
        },
    };
};
